import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '../lib/api';
import { sessionStorageManager as storage } from '../lib/storage';
import { useAuth } from './AuthContext';
import { can as canDecide } from '../lib/auth';

const ProjectContext = createContext(null);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [activeSchedule, setActiveSchedule] = useState(null);
  // Caller's role on the ACTIVE project (PROJECT_ROLE.* | null).
  // null = no project_members row: either no access (404 from the endpoint,
  // handled in loadProject's catch) or access-without-membership (admin /
  // super_admin, 200 with role: null). can() resolves both correctly via the
  // org axis, so the single null value is faithful to backend rbac.py.
  const [activeProjectRole, setActiveProjectRole] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load active project from sessionStorage on mount
  useEffect(() => {
    const savedProjectId = storage.getActiveProjectId();
    if (savedProjectId) {
      loadProject(savedProjectId).catch(() => {
        // Project not found or not accessible (e.g. different org, deleted)
        // clearActiveProject already called in loadProject catch block
        console.warn('Cleared stale activeProjectId from sessionStorage');
      });
    }
  }, []);

  // Fetch all projects
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
      return response.data;
    } catch (error) { 
      console.error('Failed to fetch projects:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load a specific project and its schedules
  const loadProject = useCallback(async (projectId) => {
    setLoading(true);
    // Reset the per-project role BEFORE any await: a switch from project A->B
    // sets activeProject=B (below) before the new role resolves, so without this
    // reset there is a render window where B's content is gated by A's role —
    // leaking/flashing edit controls for multi-project users. null = deny
    // project-work until /members/me returns the real role for B.
    setActiveProjectRole(null);
    try {
      // The PROJECT fetch is the only critical one — a 404 here means genuine
      // no-access and must bail. The schedules list is project-WORK content that
      // an org admin managing members legitimately lacks access to (404), so it
      // is best-effort and must NOT abort the project load.
      const projectRes = await api.get(`/projects/${projectId}`);
      setActiveProject(projectRes.data);
      storage.setActiveProjectId(projectId);

      try {
        const schedulesRes = await api.get(`/schedules/project/${projectId}`);
        setSchedules(schedulesRes.data);
        setActiveSchedule(schedulesRes.data.find(s => s.is_active) || null);
      } catch (schedErr) {
        setSchedules([]);
        setActiveSchedule(null);
      }

      // Caller's per-project role. 200 -> role (or null for admin/super_admin
      // with access but no membership). 404 -> no access -> null. Best-effort.
      try {
        const roleRes = await api.get(`/projects/${projectId}/members/me`);
        setActiveProjectRole(roleRes.data?.role ?? null);
      } catch (roleError) {
        setActiveProjectRole(null);
      }

      return projectRes.data;
    } catch (error) {
      console.error('Failed to load project:', error);
      clearActiveProject();
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear active project
  const clearActiveProject = useCallback(() => {
    setActiveProject(null);
    setSchedules([]);
    setActiveSchedule(null);
    setActiveProjectRole(null);
    storage.clearActiveProjectId();
  }, []);

  // Reset session-scoped project state when the authenticated user changes (F1).
  // The active project belongs to ONE user's session. AuthContext.logout() clears
  // the token + activeProjectId from sessionStorage (the reload path), but in a
  // single-page session (Sign out -> log in as another user, no full reload) the
  // provider never remounts, so without this the previous user's in-memory
  // activeProject / activeSchedule / activeProjectRole survived — a zero-access
  // admin would then see the prior plan member's project + ENABLED plan controls.
  // Clear on a transition AWAY from an established user (logout -> null, or a
  // switch to a different id). The initial null -> first-user resolution is NOT a
  // switch (prev == null), so a mount-loaded project is preserved.
  const prevUserIdRef = useRef(undefined);
  useEffect(() => {
    const uid = user?.id ?? null;
    const prev = prevUserIdRef.current;
    if (prev != null && prev !== uid) {
      clearActiveProject();
    }
    prevUserIdRef.current = uid;
  }, [user?.id, clearActiveProject]);

  // Create a new project
  const createProject = useCallback(async (projectData) => {
    try {
      // RBAC v2: the creator IS the owner — the backend stamps owner_id and
      // the PLAN membership row server-side. No caller-chosen planner.
      const response = await api.post('/projects', projectData);
      await fetchProjects();
      return response.data;
    } catch (error) {
      console.error('Failed to create project:', error);
      throw error;
    }
  }, [fetchProjects, user]);

  // Update a project
  const updateProject = useCallback(async (projectId, projectData) => {
    try {
      const response = await api.put(`/projects/${projectId}`, projectData);
      await fetchProjects();
      if (activeProject?.id === projectId) {
        setActiveProject(response.data);
      }
      return response.data;
    } catch (error) {
      console.error('Failed to update project:', error);
      throw error;
    }
  }, [fetchProjects, activeProject]);

  // Delete a project and all its associated data
  const deleteProject = useCallback(async (projectId) => {
    await api.delete(`/projects/${projectId}`);
    if (activeProject?.id === projectId) {
      clearActiveProject();
    }
    await fetchProjects();
  }, [fetchProjects, clearActiveProject, activeProject]);

  // Create a new schedule
  const createSchedule = useCallback(async (scheduleData) => {
    try {
      const response = await api.post('/schedules', scheduleData);
      if (activeProject?.id === scheduleData.project_id) {
        const schedulesRes = await api.get(`/schedules/project/${scheduleData.project_id}`);
        setSchedules(schedulesRes.data);
        // Update active schedule
        const active = schedulesRes.data.find(s => s.is_active);
        setActiveSchedule(active || null);
      }
      return response.data;
    } catch (error) {
      console.error('Failed to create schedule:', error);
      throw error;
    }
  }, [activeProject]);

  // Set active schedule for project
  const setProjectActiveSchedule = useCallback(async (projectId, scheduleId) => {
    try {
      const response = await api.put(`/projects/${projectId}/active-schedule/${scheduleId}`);
      if (activeProject?.id === projectId) {
        setActiveProject(response.data);
        const schedulesRes = await api.get(`/schedules/project/${projectId}`);
        setSchedules(schedulesRes.data);
        const active = schedulesRes.data.find(s => s.is_active);
        setActiveSchedule(active || null);
      }
      return response.data;
    } catch (error) {
      console.error('Failed to set active schedule:', error);
      throw error;
    }
  }, [activeProject]);

  // Fetch schedules for a project
  const fetchSchedules = useCallback(async (projectId) => {
    try {
      const response = await api.get(`/schedules/project/${projectId}`);
      if (activeProject?.id === projectId) {
        setSchedules(response.data);
      }
      return response.data;
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
      throw error;
    }
  }, [activeProject]);

  // Authorization for the ACTIVE project, mirroring backend rbac.py.
  // Binds the org axis (user.role) and project axis (activeProjectRole) so
  // callers ask can(Action.X) without threading roles through every component.
  const can = useCallback(
    (action) => canDecide(action, { orgRole: user?.role, isPlanner: !!user?.is_planner, projectRole: activeProjectRole }),
    [user, activeProjectRole]
  );

  const value = {
    // State
    projects,
    activeProject,
    schedules,
    activeSchedule,
    activeProjectRole,
    loading,
    // Actions
    fetchProjects,
    loadProject,
    clearActiveProject,
    createProject,
    updateProject,
    deleteProject,
    createSchedule,
    setProjectActiveSchedule,
    fetchSchedules,
    // Authorization (active project)
    can,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};
